import { type DateWithMode } from "../types/Forms";

/** Planner search times are compared at minute precision (same as the search bar UI). */
const MILLISECONDS_PER_MINUTE = 60_000;

/**
 * Floors a timestamp to the start of its local minute.
 *
 * @param timestamp Epoch milliseconds.
 * @returns Timestamp aligned to minute boundary.
 */
const floorTimestampToMinute = ( timestamp: number ): number => {
    return Math.floor( timestamp / MILLISECONDS_PER_MINUTE ) * MILLISECONDS_PER_MINUTE;
};

/**
 * Whether a planner search timestamp is strictly before the current minute.
 *
 * @param timestamp - Selected departure or arrival time in epoch milliseconds.
 * @returns `true` when the timestamp minute is before the current minute.
 */
export const isSearchTimestampInPast = ( timestamp: number ): boolean => {
    const nowMinute = floorTimestampToMinute( Date.now() );
    const timestampMinute = floorTimestampToMinute( timestamp );

    return timestampMinute < nowMinute;
};

/**
 * Returns planner date state suitable for a new itinerary search.
 *
 * @remarks When the selected time is already in the past, it is bumped to the
 * current minute so searches always query from "now" instead of stale values
 * left on screen after earlier results. Minute alignment avoids a React loop
 * where `Date.now()` from one render is always `< Date.now()` on the next.
 *
 * @param date - Current planner date with departure/arrival mode.
 * @returns The same date reference when still valid, otherwise a copy with `timestamp` set to now.
 */
export const resolveSearchDateForNewSearch = ( date: DateWithMode ): DateWithMode => {
    if ( !isSearchTimestampInPast( date.timestamp ) ) {
        return date;
    }

    return {
        ...date,
        "timestamp": floorTimestampToMinute( Date.now() ),
    };
};
